import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { TEMPLATE_MARKDOWN, downloadTextFile } from '../lib/markdown';
import { Upload, FileText, Loader2, Download, Bot, Zap } from 'lucide-react';

export const ImportMarkdownDialog = ({ open, onOpenChange, onImported, title = 'Import from markdown' }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const content = await file.text();
      setText(content);
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      toast.error('Could not read that file');
    }
    e.target.value = '';
  };

  const runImport = async () => {
    if (text.trim().length < 5) { toast.error('Paste some markdown or upload a .md file first.'); return; }
    setBusy(true);
    try {
      const res = await api.post('/import/markdown', { markdown: text });
      const a = res.data?.agents?.length || 0;
      const s = res.data?.skills?.length || 0;
      const errs = res.data?.errors || [];
      if (a === 0 && s === 0) {
        toast.error('Nothing imported — check the format (each item starts with "# Agent:" or "# Skill:").');
      } else {
        toast.success(`Imported ${a} agent${a === 1 ? '' : 's'} and ${s} skill${s === 1 ? '' : 's'}.`);
        if (errs.length) toast.warning(`${errs.length} item(s) had issues and were skipped.`);
        setText('');
        onOpenChange(false);
        onImported && onImported(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="import-markdown-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> {title}
          </DialogTitle>
          <DialogDescription>
            Upload or paste a markdown file. One file can hold several agents and skills. Imported items are private to you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept=".md,.markdown,.txt,text/markdown,text/plain" className="hidden" onChange={onFile} data-testid="import-file-input" />
            <Button variant="secondary" className="rounded-xl gap-1.5 border border-[hsl(var(--border))]" onClick={() => fileRef.current?.click()} data-testid="import-upload-button">
              <Upload className="h-4 w-4" /> Upload .md file
            </Button>
            <Button variant="ghost" className="rounded-xl gap-1.5" onClick={() => downloadTextFile('larkwell-template.md', TEMPLATE_MARKDOWN)} data-testid="import-template-button">
              <Download className="h-4 w-4" /> Download template
            </Button>
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'# Agent: Focus Coach\n- provider: openai\n- model: gpt-5.4\n\n## Persona\nYou are a warm ADHD focus coach...'}
            className="min-h-[200px] rounded-xl font-mono text-xs"
            data-testid="import-markdown-textarea"
          />

          <div className="flex items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1"><Bot className="h-3.5 w-3.5" /> Start agents with <code className="px-1">{'# Agent: Name'}</code></span>
            <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Start skills with <code className="px-1">{'# Skill: Name'}</code></span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-xl" onClick={() => onOpenChange(false)} disabled={busy} data-testid="import-cancel-button">Cancel</Button>
          <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={runImport} disabled={busy} data-testid="import-confirm-markdown-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportMarkdownDialog;
